import React from 'react';
import { PeopleListCard, PeopleListCardItem } from 'components/elements';
import {
  RightColumn,
  SideTitle,
  StatLabel,
  StatRow,
  StatValue,
  SurfaceCard,
} from '../index.styled';

interface UserRecord {
  id?: string;
  attributes?: {
    firstname?: string;
    lastname?: string;
    username?: string;
    avatarUrl?: string;
  };
}

interface GroupSidebarProps {
  organizers: UserRecord[];
  members: UserRecord[];
  managers: UserRecord[];
  totalEvents: number;
  upcomingEvents: number;
}

const renderUserName = (user: UserRecord) => {
  const attrs = user?.attributes || {};
  const fullname = `${attrs.firstname || ''} ${attrs.lastname || ''}`.trim();
  return fullname || attrs.username || 'Unknown user';
};

const toPersonItems = (users: UserRecord[], subtitleFor: (u: UserRecord) => string): PeopleListCardItem[] =>
  users
    .filter((u): u is UserRecord & { id: string } => Boolean(u.id))
    .map((u) => ({
      id: u.id,
      name: renderUserName(u),
      subtitle: subtitleFor(u),
      avatarUrl: u.attributes?.avatarUrl,
    }));

const GroupSidebar = ({ organizers, members, managers, totalEvents, upcomingEvents }: GroupSidebarProps) => {
  const organizerItems = toPersonItems(organizers.slice(0, 4), () => 'Organizer');
  const memberItems = toPersonItems(members.slice(0, 7), (m) => m.attributes?.username || 'Member');
  const managerItems = toPersonItems(managers.slice(0, 7), (m) => m.attributes?.username || 'Manager');

  return (
    <RightColumn>
      <PeopleListCard title="Organized By" items={organizerItems} />

      <PeopleListCard
        title="Members"
        meta={`${members.length} member${members.length === 1 ? '' : 's'}`}
        items={memberItems}
      />

      {managerItems.length > 0 && (
        <PeopleListCard
          title="Managers"
          meta={`${managers.length} manager${managers.length === 1 ? '' : 's'}`}
          items={managerItems}
        />
      )}

      <SurfaceCard>
        <SideTitle>Group Stats</SideTitle>
        <StatRow>
          <StatLabel>Total Events</StatLabel>
          <StatValue>{totalEvents}</StatValue>
        </StatRow>
        <StatRow>
          <StatLabel>Upcoming Events</StatLabel>
          <StatValue>{upcomingEvents}</StatValue>
        </StatRow>
        <StatRow>
          <StatLabel>Active Members</StatLabel>
          <StatValue>{members.length}</StatValue>
        </StatRow>
      </SurfaceCard>
    </RightColumn>
  );
};

export default GroupSidebar;
